'use client';

import { useEffect, useState } from 'react';
import { ApplicableSubscriptionsReadOnly } from 'hotdesk_typescript';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { SubscriptionsService } from '@/hotdesk-ui/services/subscriptions.service';
import {
    ListingService,
    MyListing
} from '@/hotdesk-ui/services/listing.service';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { getUserSubscriptionIdIn } from '@/hotdesk-ui/utils/subscriptionUtils';
import { parseData } from '@/hotdesk-ui/helpers/utils';

/**
 * Member-only data for the listing / workspace details pages, fetched on the
 * client AFTER hydration.
 *
 * These pages live under `/workspaces/*`, which Cloudflare caches at the edge
 * keyed by URL only. Anything the server renders from the caller's cookies
 * (their subscriptions, their plan-filtered "similar listings") therefore
 * gets cached and replayed to every other visitor of that URL for the TTL —
 * anonymous users were seeing a member's "Use my membership" toggle and a
 * 0-price sidebar. Keeping user-specific reads out of the server render makes
 * the SSR HTML identical for everyone and safe to cache; the member view
 * layers on once the persisted redux user is available.
 */

const buildSubscriptionsService = async (languageCode: string) =>
    new SubscriptionsService(
        await new ApiConfigration().getApiConfig(languageCode)
    );

const buildListingService = async (languageCode: string) =>
    new ListingService(await new ApiConfigration().getApiConfig(languageCode));

/**
 * The user's subscriptions applicable to `listingId`, or null when logged
 * out / not yet loaded. Drives the membership toggle, promo pricing and the
 * "Membership Applicable" banner.
 */
export const useListingApplicableSubscriptions = (
    listingId: number | undefined,
    languageCode: string
): ApplicableSubscriptionsReadOnly | null => {
    const userId = useAppSelector(
        (state: RootState) => state.auth.user?.id
    );
    const [applicable, setApplicable] =
        useState<ApplicableSubscriptionsReadOnly | null>(null);

    useEffect(() => {
        if (!userId || !listingId) {
            setApplicable(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const service = await buildSubscriptionsService(languageCode);
                const res = await service.getUserSubscription(
                    listingId.toString(),
                    userId.toString()
                );
                if (!cancelled) setApplicable(parseData(res));
            } catch (error) {
                console.error('Error fetching listing subscriptions:', error);
                if (!cancelled) setApplicable(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, listingId, languageCode]);

    return applicable;
};

/**
 * Nearby listings for a subscriber are narrowed to the ones their plan
 * covers (`userSubscriptionIdIn` is a hard filter on the BE). The server
 * renders the anonymous, unfiltered set; this swaps in the plan-filtered set
 * once a subscriber's session is known. `filters` must be referentially
 * stable (memoized by the caller). `excludeBy` drops the page's own row:
 * the listing page excludes by listing id, the workspace page by workspace id.
 */
export const useSubscriberSimilarListings = (
    baseListings: MyListing[],
    filters: Record<string, unknown> | null,
    excludeId: number | undefined,
    excludeBy: 'listing' | 'workspace',
    languageCode: string
): MyListing[] => {
    const user = useAppSelector((state: RootState) => state.auth.user);
    const subscriptionIdIn = getUserSubscriptionIdIn(user);
    const [listings, setListings] = useState<MyListing[]>(baseListings);

    useEffect(() => {
        setListings(baseListings);
    }, [baseListings]);

    useEffect(() => {
        if (!subscriptionIdIn || !filters) return;
        let cancelled = false;
        (async () => {
            try {
                const service = await buildListingService(languageCode);
                const res = await service.getallListings({
                    ...filters,
                    userSubscriptionIdIn: subscriptionIdIn
                });
                if (cancelled) return;
                setListings(
                    parseData(
                        res.results.filter((item) =>
                            excludeBy === 'workspace'
                                ? item?.workspace?.id !== excludeId
                                : item?.id !== excludeId
                        )
                    )
                );
            } catch (error) {
                // Enhancement only — keep the anonymous set on failure.
                console.error('Error fetching subscriber listings:', error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [subscriptionIdIn, filters, excludeId, excludeBy, languageCode]);

    return listings;
};
